/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  if (customElements.get('quick-add-modal')) return;

  var esc = function (value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  var motionOn = function () {
    return window.gsap && document.documentElement.classList.contains('motion');
  };

  var ICON_PLUS =
    '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14"/></svg>';
  var ICON_MINUS =
    '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M5 12h14"/></svg>';

  class QuickAddModal extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('.modal');
      this.panel = this.querySelector('.modal__panel');
      this.content = this.querySelector('[data-quick-add-content]');
      this.strings = this.readStrings();
      this.isOpen = false;
      this.onKeydown = (e) => {
        if (e.key === 'Escape') this.close();
      };
      this.querySelectorAll('[data-quick-add-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      if (window.AirX) {
        window.AirX.on('overlay:backdrop-click', () => {
          if (this.isOpen) this.close();
        });
      }
    }

    readStrings() {
      var defaults = {
        addToCart: 'Add to cart',
        soldOut: 'Sold out',
        unavailable: 'Unavailable',
        quantity: 'Quantity',
        increase: 'Increase quantity',
        decrease: 'Decrease quantity',
        viewDetails: 'View full details',
        error: 'Something went wrong. Please try again.',
        loading: 'Loading options…',
        regularPrice: 'Regular price',
        salePrice: 'Sale price',
      };
      var el = this.querySelector('[data-quick-add-strings]');
      if (!el) return defaults;
      try {
        return Object.assign(defaults, JSON.parse(el.textContent));
      } catch (err) {
        return defaults;
      }
    }

    async openFor(trigger) {
      var url = trigger.getAttribute('data-product-url') || trigger.getAttribute('href');
      if (!url) return;
      this.trigger = trigger;
      this.productUrl = url.split('?')[0].replace(/\/$/, '');
      this.open();
      this.renderSkeleton();
      try {
        var res = await fetch(this.productUrl + '.js');
        if (!res.ok) throw new Error(res.statusText);
        this.product = await res.json();
        this.renderPicker();
      } catch (err) {
        this.renderFallback();
      }
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      window.AirX.overlayOpened('quick-add-modal');
      document.addEventListener('keydown', this.onKeydown);
      if (motionOn()) {
        this.panel.style.transition = 'none';
        window.gsap.fromTo(
          this.panel,
          { opacity: 0, y: 32, scale: 0.96 },
          { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'expo.out', clearProps: 'opacity,transform' }
        );
      }
      window.AirX.trapFocus(this.panel, this.querySelector('[data-quick-add-close]'));
      window.AirX.emit('quick-add:open', { modal: this });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      document.removeEventListener('keydown', this.onKeydown);
      window.AirX.overlayClosed('quick-add-modal');
      var finish = () => {
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        this.panel.style.transition = '';

        window.AirX.releaseFocus(this.panel);
      };
      if (motionOn()) {
        window.gsap.to(this.panel, {
          opacity: 0,
          y: 18,
          scale: 0.97,
          duration: 0.25,
          ease: 'power2.in',
          onComplete: () => {
            finish();
            window.gsap.set(this.panel, { clearProps: 'opacity,transform' });
          },
        });
      } else {
        finish();
      }
    }

    renderSkeleton() {
      this.content.innerHTML =
        '<div class="quick-add__grid" aria-busy="true">' +
        '<div class="quick-add__media artwork-tile"><div class="quick-add__skeleton-media skeleton"></div></div>' +
        '<div class="quick-add__body">' +
        '<span class="visually-hidden">' + esc(this.strings.loading) + '</span>' +
        '<div class="skeleton quick-add__skeleton-line quick-add__skeleton-line--title"></div>' +
        '<div class="skeleton quick-add__skeleton-line quick-add__skeleton-line--price"></div>' +
        '<div class="quick-add__skeleton-pills">' +
        new Array(4).join('<div class="skeleton quick-add__skeleton-pill"></div>') +
        '</div>' +
        '<div class="quick-add__skeleton-pills">' +
        new Array(3).join('<div class="skeleton quick-add__skeleton-pill"></div>') +
        '</div>' +
        '<div class="skeleton quick-add__skeleton-line quick-add__skeleton-line--cta"></div>' +
        '</div>' +
        '</div>';
    }

    renderFallback() {
      this.content.innerHTML =
        '<div class="quick-add__body quick-add__fallback">' +
        '<p class="quick-add__error">' + esc(this.strings.error) + '</p>' +
        '<a class="btn btn--secondary" href="' + esc(this.productUrl) + '">' + esc(this.strings.viewDetails) + '</a>' +
        '</div>';
      window.AirX.trapFocus(this.panel, this.querySelector('[data-quick-add-close]'));
    }

    renderPicker() {
      var p = this.product;
      var s = this.strings;
      this.optionNames = (p.options || []).map(function (o) {
        return typeof o === 'string' ? o : o.name;
      });
      var first = p.variants.find(function (v) { return v.available; }) || p.variants[0];
      this.selected = this.optionNames.map(function (name, i) {
        return first ? first['option' + (i + 1)] : null;
      });
      var singleDefault =
        p.variants.length === 1 &&
        this.optionNames.length <= 1 &&
        first &&
        (first.option1 === 'Default Title' || !first.option1);

      var image = p.featured_image
        ? '<img src="' + esc(this.sizedImage(p.featured_image, 640)) + '" alt="' + esc(p.title) + '" loading="eager" data-qa-image>'
        : '';

      var optionsHtml = '';
      if (!singleDefault) {
        optionsHtml = this.optionNames
          .map((name, i) => {
            var isDepth = /^depth$/i.test(name);
            var pills = this.valuesFor(i)
              .map(function (v) {
                return (
                  '<button type="button" class="quick-add__pill" data-qa-option data-index="' + i +
                  '" data-value="' + esc(v) + '" aria-pressed="false">' + esc(v) + '</button>'
                );
              })
              .join('');
            return (
              '<div class="quick-add__option" role="group" aria-labelledby="QuickAddOption' + i + '">' +
              '<div class="quick-add__label" id="QuickAddOption' + i + '">' +
              '<span>' + esc(name) + '</span>' +
              '<span class="quick-add__selected" data-qa-selected="' + i + '"></span>' +
              '</div>' +
              '<div class="' + (isDepth ? 'quick-add__segment' : 'quick-add__pills') + '">' + pills + '</div>' +
              '</div>'
            );
          })
          .join('');
      }

      this.content.innerHTML =
        '<div class="quick-add__grid">' +
        '<div class="quick-add__media artwork-tile">' + image + '</div>' +
        '<div class="quick-add__body">' +
        '<a class="quick-add__title" href="' + esc(this.productUrl) + '">' + esc(p.title) + '</a>' +
        '<div class="price quick-add__price" data-qa-price></div>' +
        optionsHtml +
        '<div class="quick-add__row">' +
        '<quantity-input class="quantity">' +
        '<button type="button" class="quantity__button" name="minus" aria-label="' + esc(s.decrease) + '">' + ICON_MINUS + '</button>' +
        '<input class="quantity__input" type="number" name="quantity" inputmode="numeric" value="1" min="1" aria-label="' + esc(s.quantity) + '">' +
        '<button type="button" class="quantity__button" name="plus" aria-label="' + esc(s.increase) + '">' + ICON_PLUS + '</button>' +
        '</quantity-input>' +
        '<button type="button" class="btn btn--primary quick-add__atc" data-qa-atc><span data-qa-atc-label></span></button>' +
        '</div>' +
        '<p class="quick-add__error" data-qa-error hidden></p>' +
        '<a class="quick-add__details link-drift" href="' + esc(this.productUrl) + '">' + esc(s.viewDetails) + '</a>' +
        '</div>' +
        '</div>';

      this.content.querySelectorAll('[data-qa-option]').forEach((pill) =>
        pill.addEventListener('click', () => {
          this.selected[Number(pill.dataset.index)] = pill.dataset.value;
          this.update();
        })
      );
      this.content.querySelector('[data-qa-atc]').addEventListener('click', () => this.addToCart());

      this.update();
      this.entrance();
      window.AirX.trapFocus(this.panel, this.querySelector('[data-quick-add-close]'));
    }

    entrance() {
      if (!motionOn()) return;
      var items = this.content.querySelectorAll('.quick-add__body > *');
      if (!items.length) return;
      window.gsap.fromTo(
        items,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', stagger: 0.05, clearProps: 'opacity,transform' }
      );
    }

    valuesFor(i) {
      var opt = this.product.options && this.product.options[i];
      if (opt && typeof opt === 'object' && Array.isArray(opt.values)) return opt.values;
      var seen = [];
      this.product.variants.forEach(function (v) {
        var val = v['option' + (i + 1)];
        if (val != null && seen.indexOf(val) === -1) seen.push(val);
      });
      return seen;
    }

    currentVariant() {
      var self = this;
      var count = this.optionNames.length;
      return (
        this.product.variants.find(function (v) {
          for (var i = 0; i < count; i++) {
            if (self.selected[i] != null && v['option' + (i + 1)] !== self.selected[i]) return false;
          }
          return true;
        }) || null
      );
    }

    valueAvailable(i, value) {
      var self = this;
      return this.product.variants.some(function (v) {
        if (v['option' + (i + 1)] !== value) return false;
        for (var j = 0; j < self.optionNames.length; j++) {
          if (j === i) continue;
          if (self.selected[j] != null && v['option' + (j + 1)] !== self.selected[j]) return false;
        }
        return v.available;
      });
    }

    priceHtml(variant) {
      var s = this.strings;
      var money = function (cents) {
        return window.AirX && window.AirX.formatMoney
          ? window.AirX.formatMoney(cents)
          : '$' + (cents / 100).toFixed(2);
      };
      if (!variant) return '<span class="price__unavailable">' + esc(s.unavailable) + '</span>';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        return (
          '<span class="visually-hidden">' + esc(s.salePrice) + '</span>' +
          '<span class="price__sale">' + money(variant.price) + '</span>' +
          '<span class="visually-hidden">' + esc(s.regularPrice) + '</span>' +
          '<s class="price__compare">' + money(variant.compare_at_price) + '</s>'
        );
      }
      return '<span class="price__regular">' + money(variant.price) + '</span>';
    }

    update() {
      var s = this.strings;
      var variant = (this.variant = this.currentVariant());

      var priceEl = this.content.querySelector('[data-qa-price]');
      if (priceEl) priceEl.innerHTML = this.priceHtml(variant);

      this.content.querySelectorAll('[data-qa-option]').forEach((pill) => {
        var i = Number(pill.dataset.index);
        var isSelected = this.selected[i] === pill.dataset.value;
        pill.classList.toggle('is-selected', isSelected);
        pill.setAttribute('aria-pressed', String(isSelected));
        pill.classList.toggle('is-unavailable', !this.valueAvailable(i, pill.dataset.value));
      });

      this.content.querySelectorAll('[data-qa-selected]').forEach((el) => {
        el.textContent = this.selected[Number(el.dataset.qaSelected)] || '';
      });

      var atc = this.content.querySelector('[data-qa-atc]');
      var label = this.content.querySelector('[data-qa-atc-label]');
      if (!atc || !label) return;
      if (!variant) {
        atc.setAttribute('disabled', '');
        label.textContent = s.unavailable;
      } else if (!variant.available) {
        atc.setAttribute('disabled', '');
        label.textContent = s.soldOut;
      } else {
        atc.removeAttribute('disabled');
        label.textContent = s.addToCart;
      }

      var img = this.content.querySelector('[data-qa-image]');
      if (img && variant && variant.featured_image && variant.featured_image.src) {
        img.src = this.sizedImage(variant.featured_image.src, 640);
      }
    }

    async addToCart() {
      var variant = this.variant || this.currentVariant();
      if (!variant || !variant.available) return;
      var atc = this.content.querySelector('[data-qa-atc]');
      var errorEl = this.content.querySelector('[data-qa-error]');
      var qtyInput = this.content.querySelector('input[name="quantity"]');
      var quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      if (errorEl) errorEl.hidden = true;
      atc.classList.add('btn--loading');
      atc.setAttribute('aria-disabled', 'true');
      try {
        await window.AirX.cart.add({ id: variant.id, quantity: quantity });
        var img = this.content.querySelector('[data-qa-image]');
        if (window.AirX.flyToCart && img) window.AirX.flyToCart(img);
        window.AirX.emit('quick-add:added', { product: this.product, variant: variant, quantity: quantity });
        this.close();
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = (err && err.message) || this.strings.error;
          errorEl.hidden = false;
        }
      } finally {
        atc.classList.remove('btn--loading');
        atc.removeAttribute('aria-disabled');
      }
    }

    sizedImage(src, width) {
      try {
        var u = new URL(src, window.location.origin);
        u.searchParams.set('width', String(width));
        return u.href;
      } catch (err) {
        return src;
      }
    }
  }

  customElements.define('quick-add-modal', QuickAddModal);

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-add]');
    if (!trigger) return;
    var url = trigger.getAttribute('data-product-url') || trigger.getAttribute('href');
    var modal = document.querySelector('quick-add-modal');
    if (!modal || typeof modal.openFor !== 'function' || !window.AirX || !window.AirX.cart) {
      if (url) window.location.href = url.split('?')[0];
      return;
    }
    e.preventDefault();
    modal.openFor(trigger);
  });
})();
